/**
 * Token persistence.
 *
 * The session token returned by Convex `auth:signIn` / `auth:signUp` is
 * stored in localStorage under a single namespaced key. We use the raw
 * Storage API rather than a wrapper library to keep the surface tiny and
 * SSR-safe (Vite runs client-only, but we still guard against the
 * Storage API being unavailable in some embedded webviews).
 *
 * On `superseded` (another device logged in), `useSessionGuard` clears
 * the token via `clearToken()`.
 */

const TOKEN_KEY = 'aura.session.token.v1';

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readToken(): string | null {
  const s = safeStorage();
  if (!s) return null;
  try {
    const v = s.getItem(TOKEN_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function writeToken(token: string): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(TOKEN_KEY, token);
  } catch {
    /* swallow quota errors */
  }
}

export function clearToken(): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
